import { Spine, Skin } from "@esotericsoftware/spine-pixi-v7";
import * as PIXI from "pixi.js";
import anime from "animejs";
import {
  setShowHoldAndWinIntro,
  setShowTransitionDialog,
} from "../../../State/actions";
import {
  // clearHoldAndWinReload, — the close leaves the round parked (see finishAnimation)
  setShowHoldAndWinReload,
} from "../../../State/Slices/holdAndWinSlice";
import { Settings } from "../../../settings";
import { PixiSettings } from "../../../pixiSettings";
import { soundManager } from "../../../SoundManager";
import { SOUND_NAME } from "../../../sounds";
import TickerTimeout from "../../../utils/TickerTimeout";
import SpineAtlasUploader from "../../../utils/SpineAtlasUploader";
import { FONT_STYLES, scaleStyle } from "../../../fontStyles";
// import { FS_NUMBER_FONT_SIZE } from "../../../Games/Main/GambleScatter/GambleLevelOverlay"; // the FS plate's number size
import attachTapToContinue from "../../../utils/attachTapToContinue";
import store from "../../../State/Store/store";

/**
 * HoldAndWinIntroController — the Cleopatra's Respins intro popup.
 *
 * A COPY of FreeSpinIntroDialogController: the same spine, the same sequence
 * (in -> idle, tap -> out -> the wipe), the same sizing table. Only the copy
 * and the number differ (GDD §11.5):
 *
 *   CONGRATULATIONS! / YOU WON / 3 / CLEOPATRA'S RESPINS
 *
 * The number is the server's respins_left on the trigger (always 3, GDD I6).
 * The plate uses the `high` level skin — H&W has no package tiers.
 *
 * A reload on the trigger replays the trigger round and this popup plays
 * exactly as live.
 *
 * The RELOAD variant (2026-09-29) — a reload mid-feature, the room already
 * restored behind it, as the FS reload popup does:
 *
 *   YOU HAVE / n / RESPIN(S) LEFT         TAP TO CONTINUE
 *
 * n is the server's respins_left; the word follows it (1 RESPIN, 2 RESPINS).
 * Closing it plays no wipe and starts nothing: the round stays parked on the
 * glowing play button until the player presses it, as in Mad Labs. No
 * auto-hide either.
 */

// How long the popup waits for a tap before closing itself, ms. GDD §11.5 —
// applied unconditionally, as the FS intro does.
const AUTO_HIDE_MS = 10000;

// Same table as the FS intro, value for value.
// const DEVICE_MULTIPLIERS_MOBILE_SMALL_PORTRAIT = { scale: 1.0, offset: 0.0, buttonYOffset: 0.0 };
const DEVICE_MULTIPLIERS_MOBILE_SMALL_PORTRAIT = {
  scale: 0.8,
  offset: 0.0,
  buttonYOffset: 0.0,
}; // 0.9 everywhere to start — the new art is big (2026-09-30)
// const DEVICE_MULTIPLIERS_MOBILE_SMALL_LANDSCAPE = { scale: 1.0, offset: 0.0, buttonYOffset: -34.8 };
const DEVICE_MULTIPLIERS_MOBILE_SMALL_LANDSCAPE = {
  scale: 0.65,
  offset: 0.0,
  buttonYOffset: -34.8,
}; // 0.9 everywhere to start — the new art is big (2026-09-30)
// const DEVICE_MULTIPLIERS_MOBILE_PORTRAIT = { scale: 1.0, offset: 0.0, buttonYOffset: 0.0 };
const DEVICE_MULTIPLIERS_MOBILE_PORTRAIT = {
  scale: 0.8,
  offset: 0.0,
  buttonYOffset: 0.0,
}; // 0.9 everywhere to start — the new art is big (2026-09-30)
// const DEVICE_MULTIPLIERS_MOBILE_LANDSCAPE = { scale: 0.9, offset: 0.0, buttonYOffset: -34.8 };
// const DEVICE_MULTIPLIERS_MOBILE_LANDSCAPE = { scale: 0.9, offset: 0.0, buttonYOffset: -34.8 }; // 0.9 everywhere to start — the new art is big (2026-09-30)
const DEVICE_MULTIPLIERS_MOBILE_LANDSCAPE = {
  scale: 0.65,
  offset: 0.0,
  buttonYOffset: -34.8,
}; // 0.8 on a landscape phone (Vladimir, 2026-09-30)
// const DEVICE_MULTIPLIERS_TABLET_PORTRAIT = { scale: 1.0, offset: 0.0, buttonYOffset: 0.0 };
const DEVICE_MULTIPLIERS_TABLET_PORTRAIT = {
  scale: 0.9,
  offset: 0.0,
  buttonYOffset: 0.0,
}; // 0.9 everywhere to start — the new art is big (2026-09-30)
// const DEVICE_MULTIPLIERS_TABLET_LANDSCAPE = { scale: 1.0, offset: 0.0, buttonYOffset: 0.0 };
const DEVICE_MULTIPLIERS_TABLET_LANDSCAPE = {
  scale: 0.9,
  offset: 0.0,
  buttonYOffset: 0.0,
}; // 0.9 everywhere to start — the new art is big (2026-09-30)
// const DEVICE_MULTIPLIERS_DESKTOP = { scale: 0.9, offset: 0.0, buttonYOffset: -34.8 };
const DEVICE_MULTIPLIERS_DESKTOP = {
  scale: 0.65,
  offset: 0.0,
  buttonYOffset: -34.8,
}; // 0.9 everywhere to start — the new art is big (2026-09-30)

// const SPINE_NAME = "free_spin_intro_dialog";
// Its own spine now (animator, 2026-09-30): YOU HAVE / [n in the eye] /
// FREE SPINS LEFT art — txt_slot_you_have, txt_slot_spins_respins_amount,
// txt_slot_spins_respins, txt_slot_taptostart. No level skins. The tap bone is
// a HAND EDIT (0, -471.38) — the FS intro's, so the tap line sits on the same
// screen line as every other popup (the export had it at (3, -856.5)).
const SPINE_NAME = "hold_and_win_intro";

const ANIM = {
  IN: ["in", "free_spin_intro_in"],
  IDLE: ["idle", "free_spin_intro_idle"],
  OUT: ["out", "free_spin_intro_out"],
};

// const BUTTON_SLOTS = ["txt_slot_tap_to_continue", "txt_slot_taptocontinue"];
const BUTTON_SLOTS = [
  "txt_slot_taptostart",
  "txt_slot_tap_to_continue",
  "txt_slot_taptocontinue",
];
const CONGRATS_SLOTS = ["txt_slot_congratulations", "txt_slot_congrats"];
// const YOU_WON_SLOTS = ["txt_slot_you_won", "txt_slot_youwon"];
// const LABEL_SLOTS = ["txt_slot_freespins", "txt_slot_free_games"];
const YOU_WON_SLOTS = [
  "txt_slot_you_have",
  "txt_slot_you_won",
  "txt_slot_youwon",
];
const LABEL_SLOTS = [
  "txt_slot_spins_respins",
  "txt_slot_freespins",
  "txt_slot_free_games",
];

const LEVEL = "high";
const LEVEL_SKIN = "levels/high";
// const VALUE_SLOT = "txt_slot_value_high";
const VALUE_SLOT = "txt_slot_spins_respins_amount"; // the number in the eye
// const VALUE_FONT_SCALE = 2.0; // Same as the FS intro.
// This spine's text sizes (its art is drawn bigger than the FS intro plate,
// measured from the design: the lines ~140, the number ~200 spine units).
// The lines had 84 on the FS plate.
const LINE_FONT_SIZE = 140;
// const NUMBER_FONT_SIZE = 200;
const NUMBER_FONT_SIZE = 240; // the design's number is ~0.44 of the eye ring; 200 read 0.33
// The text slots carry no bone length here — the widest a line may be, in
// spine units (the wings' span).
const LINE_MAX_WIDTH = 1100;

const DEFAULT_SENTENCE =
  "Congratulations!\nYou won\n{{count}}\nCleopatra's Respins";
// The reload sentence, the FS reload's shape (you_have_freespins_left). The
// English default picks the noun itself, so it is right with no ICU at all.
const reloadDefault = (count) =>
  count === 1 ? "You have\n1\nRespin left" : `You have\n${count}\nRespins left`;

class HoldAndWinIntroController {
  constructor(
    container,
    dimensionsContext,
    dispatch,
    symbolHeight,
    overlayRef,
    t,
    variant = "intro", // "intro" | "reload" — see the header
  ) {
    this.isReload = variant === "reload";
    this.container = container;
    this.dimensionsContext = dimensionsContext;
    this.dispatch = dispatch;
    this.symbolHeight = symbolHeight;
    this.overlayRef = overlayRef;
    this.t = t;
    this.dimensions = dimensionsContext.dimensions;

    this.introSpine = null;
    this.isPlaying = false;
    this.initialized = false;
    this.isWaitingForClick = false;

    this.canSkipAnimation = false;
    this.debounceTimeout = null;
    this.skipProtectionTimeout = null;
    this.currentTimeout = null;
    this.autoHideTimeout = null;

    this.buttonContainer = null;
    this.buttonText = null;
    this.textContainers = [];
    this._respins = 3;

    this._configCache = null;
    this._lastDevice = null;
    this._lastOrientation = null;
    this._lastIsMobileSmall = null;

    this.initializeIntroSpine();
  }

  // ── Sizing (FS intro) ──

  getDeviceMultipliers() {
    const { device, orientation, isMobileSmall } = this.dimensionsContext || {};
    if (
      this._configCache !== null &&
      this._lastDevice === device &&
      this._lastOrientation === orientation &&
      this._lastIsMobileSmall === isMobileSmall
    ) {
      return this._configCache;
    }

    const isPortrait = orientation === PixiSettings.ORIENTATION.PORTRAIT;
    let multipliers;
    if (device === PixiSettings.DEVICE.MOBILE) {
      if (isMobileSmall) {
        multipliers = isPortrait
          ? DEVICE_MULTIPLIERS_MOBILE_SMALL_PORTRAIT
          : DEVICE_MULTIPLIERS_MOBILE_SMALL_LANDSCAPE;
      } else {
        multipliers = isPortrait
          ? DEVICE_MULTIPLIERS_MOBILE_PORTRAIT
          : DEVICE_MULTIPLIERS_MOBILE_LANDSCAPE;
      }
    } else if (device === PixiSettings.DEVICE.TABLET) {
      multipliers = isPortrait
        ? DEVICE_MULTIPLIERS_TABLET_PORTRAIT
        : DEVICE_MULTIPLIERS_TABLET_LANDSCAPE;
    } else {
      multipliers = DEVICE_MULTIPLIERS_DESKTOP;
    }

    this._configCache = multipliers;
    this._lastDevice = device;
    this._lastOrientation = orientation;
    this._lastIsMobileSmall = isMobileSmall;
    return multipliers;
  }

  applyScaleAndCenter() {
    if (!this.introSpine || !this.dimensions) return;

    const nativeSize = Settings.SYMBOL_HEIGHT;
    const targetSize = this.symbolHeight || nativeSize;
    let scale = Math.max(0.1, Math.min(targetSize / nativeSize, 2));
    const { scale: deviceScale, offset: deviceOffset } =
      this.getDeviceMultipliers();
    scale *= deviceScale;

    this.introSpine.scale.set(scale);
    this.introSpine.x = 0;
    this.introSpine.y = -(this.symbolHeight || 0) * deviceOffset;

    this._fitTapButton?.(this.dimensions);
    this.applyButtonOffset();
  }

  updateDimensions(dimensionsContext, symbolHeight) {
    const oldDimensions = this.dimensionsContext?.dimensions;
    const newDimensions = dimensionsContext?.dimensions;
    const deviceChanged =
      this.dimensionsContext?.device !== dimensionsContext?.device;
    const dimensionsChanged =
      !oldDimensions ||
      oldDimensions.width !== newDimensions?.width ||
      oldDimensions.height !== newDimensions?.height ||
      deviceChanged ||
      this.dimensionsContext?.orientation !== dimensionsContext?.orientation;
    const symbolHeightChanged = this.symbolHeight !== symbolHeight;
    if (!dimensionsChanged && !symbolHeightChanged) return;

    this.dimensionsContext = dimensionsContext;
    this.dimensions = dimensionsContext.dimensions;
    this.symbolHeight = symbolHeight;
    this._configCache = null;

    if (deviceChanged && this.buttonText) this.updateButtonText();
    if (this.introSpine && this.symbolHeight > 0) this.applyScaleAndCenter();
  }

  // ── Spine ──

  initializeIntroSpine() {
    try {
      this.introSpine = this.createIntroSpine();
      if (!this.introSpine?.skeleton?.data || !this.introSpine.state) return;

      this.container.addChild(this.introSpine);
      // Alpha 0, not 0.001 — at 0 PIXI skips the whole subtree.
      this.introSpine.alpha = 0;
      this.introSpine.autoUpdate = false;
      this.introSpine.zIndex = 20;

      this.applyScaleAndCenter();
      this.initialized = true;

      // this.applyLevelSkin(); — the FS plate's level skin; this spine has none
      this.warmUpIntroSpine();
      this.initializeButton();
      this.initializeTexts();
    } catch (error) {
      // Silent fail, as the FS intro.
    }
  }

  createIntroSpine() {
    try {
      const skeleton = `${SPINE_NAME}-skeleton`;
      const atlas = `${SPINE_NAME}-atlas`;
      if (!PIXI.Assets.cache.has(skeleton) || !PIXI.Assets.cache.has(atlas)) {
        return null;
      }
      const spine = Spine.from({ skeleton, atlas });
      return spine?.skeleton?.data?.animations ? spine : null;
    } catch (error) {
      return null;
    }
  }

  _resolveAnim(candidates) {
    const animations = this.introSpine?.skeleton?.data?.animations;
    if (!animations) return null;
    for (const name of candidates) {
      if (animations.find((a) => a.name === name)) return name;
    }
    return null;
  }

  _resolveSlot(candidates) {
    const skeleton = this.introSpine?.skeleton;
    if (!skeleton) return null;
    for (const name of candidates) {
      if (skeleton.findSlot(name)) return name;
    }
    return null;
  }

  /** `default` + the level plate, COMBINED (FS intro). */
  applyLevelSkin() {
    if (!this.introSpine?.skeleton) return;
    const data = this.introSpine.skeleton.data;
    const base = data.findSkin("default");
    const levelSkin = data.findSkin(LEVEL_SKIN);
    if (!levelSkin) return;

    const combined = new Skin("hold_and_win_intro_combined");
    if (base) combined.addSkin(base);
    combined.addSkin(levelSkin);
    this.introSpine.skeleton.setSkin(combined);
    this.introSpine.skeleton.setSlotsToSetupPose();
  }

  warmUpIntroSpine() {
    if (!this.introSpine?.state) return;
    try {
      const first = this.introSpine.skeleton.data.animations[0]?.name;
      if (first) {
        this.introSpine.autoUpdate = true;
        this.introSpine.state.setAnimation(0, first, false);
        this.introSpine.state.update(0.016);
        this.introSpine.skeleton.updateWorldTransform(
          this.introSpine.skeleton.physics !== undefined ? 2 : 0,
        );
      }
      this.introSpine.alpha = 0;
      this.introSpine.autoUpdate = false;
      SpineAtlasUploader.upload(SPINE_NAME);
    } catch {
      // Optimisation only.
    }
  }

  getSpineAnimationDuration(name) {
    const animation = this.introSpine?.skeleton?.data?.animations?.find(
      (a) => a.name === name,
    );
    return animation?.duration ? Math.round(animation.duration * 1000) : 1000;
  }

  // ── Button and texts ──

  initializeButton() {
    const built = attachTapToContinue({
      spine: this.introSpine,
      slotNames: BUTTON_SLOTS,
      label: this._buttonLabel(),
      name: "hold_and_win_intro_continue_attached_to_slot",
      onClick: () => {
        if (this.handleUserClick()) {
          soundManager.play(SOUND_NAME.tap_to_continue);
        }
      },
    });
    if (!built) return;

    this.buttonContainer = built.container;
    this.buttonText = built.text;
    this._fitTapButton = built.fit;
    this._revealMs = built.revealMs;
    this._setTapOffsetY = built.setOffsetY;
    this.buttonContainer.alpha = 0;
    this._fitTapButton(this.dimensions);
    this.applyButtonOffset();
  }

  applyButtonOffset() {
    if (!this._setTapOffsetY) return;
    const { buttonYOffset = 0 } = this.getDeviceMultipliers();
    this._setTapOffsetY(buttonYOffset);
  }

  _buttonLabel() {
    // GDD §11.5 CTA. Nothing starts on a reload — the round continues.
    return this.t(
      this.isReload ? "tap_to_continue" : "tap_to_start",
    ).toUpperCase();
  }

  updateButtonText() {
    if (!this.buttonText) return;
    this.buttonText.text = this._buttonLabel();
    this._fitTapButton?.(this.dimensions);
  }

  /**
   * The plate's lines, from ONE sentence (the FS intro's _plateLines):
   *   Congratulations! / You won / {count} / Cleopatra's Respins
   * The count line is drawn by the value slot, so it is dropped here.
   */
  _plateLines() {
    // Reload: You have / {count} / Respin(s) left
    const sentence = this.isReload
      ? this.t("you_have_respins_left", {
          count: this._respins,
          defaultValue: reloadDefault(this._respins),
        })
      : this.t("congrats_you_won_respins", {
          count: this._respins,
          defaultValue: DEFAULT_SENTENCE,
        });
    const lines = String(sentence).split("\n");
    return {
      congrats: (lines.length > 3 ? lines[0] : "").toUpperCase(),
      top: (lines[lines.length - 3] ?? "").toUpperCase(),
      bottom: (lines[lines.length - 1] ?? "").toUpperCase(),
    };
  }

  initializeTexts() {
    if (!this.introSpine?.skeleton) return;
    const plate = this._plateLines();

    // The reload has no congratulation — nothing was just won.
    if (!this.isReload) {
      this.congratsText = this._addSlotText(
        this._resolveSlot(CONGRATS_SLOTS),
        plate.congrats,
        FONT_STYLES.freeSpinsCongrats,
        111,
      );
    }
    this.leadText = this._addSlotText(
      this._resolveSlot(YOU_WON_SLOTS),
      plate.top,
      FONT_STYLES.freeSpinsText,
      // 84,
      LINE_FONT_SIZE,
    );
    this.labelText = this._addSlotText(
      this._resolveSlot(LABEL_SLOTS),
      plate.bottom,
      FONT_STYLES.freeSpinsText,
      // 84,
      LINE_FONT_SIZE,
    );
    this.respinsText = this._addSlotText(
      VALUE_SLOT,
      String(this._respins),
      FONT_STYLES.gambleLevel?.[LEVEL] || FONT_STYLES.winCelebrationCounter,
      // Math.round(FS_NUMBER_FONT_SIZE * VALUE_FONT_SCALE),
      NUMBER_FONT_SIZE,
    );
  }

  _addSlotText(slotName, text, style, fontSize) {
    if (!slotName || !style || !this.introSpine?.skeleton) return null;
    const slot = this.introSpine.skeleton.findSlot(slotName);
    if (!slot) return null;

    const container = new PIXI.Container();
    container.name = `${slotName}_text_attached_to_slot`;
    const textObj = new PIXI.Text(
      text,
      new PIXI.TextStyle(scaleStyle(style, fontSize)),
    );
    textObj.resolution = 2;
    textObj.anchor.set(0.5);
    container.addChild(textObj);
    this._fitToBone(container, textObj, slot);

    this.introSpine.addSlotObject(slotName, container);
    this.textContainers.push(container);
    return textObj;
  }

  /** The bone's length is the artist's room for the text. */
  _fitToBone(container, textObj, slot) {
    // const boneWidth = slot.bone?.data?.length || 0;
    const boneWidth = slot.bone?.data?.length || LINE_MAX_WIDTH;
    // container.scale.set(1);
    // if (boneWidth > 0 && textObj.width > boneWidth) {
    //   container.scale.set(boneWidth / textObj.width);
    // }
    // On the TEXT, not the container: spine-pixi writes the bone's scale onto
    // a slot object every frame, so a fit on the container never held (the
    // bottom line ran ~1.4x too wide, 2026-09-30).
    textObj.scale.set(1);
    if (boneWidth > 0 && textObj.width > boneWidth) {
      textObj.scale.set(boneWidth / textObj.width);
    }
  }

  /** The respins count, from the holdAndWin slice, before the popup shows. */
  updateRespins(count) {
    // A reload shows what is LEFT, whatever it is; the intro is always 3.
    this._respins = this.isReload ? (count ?? 0) : count > 0 ? count : 3;
    if (this.respinsText) this.respinsText.text = String(this._respins);
    // The word follows the number (plural).
    const plate = this._plateLines();
    if (this.labelText) {
      this.labelText.text = plate.bottom;
      this._fitToBone(
        this.labelText.parent,
        this.labelText,
        this.introSpine.skeleton.findSlot(this._resolveSlot(LABEL_SLOTS)),
      );
    }
  }

  showButton() {
    if (!this.buttonContainer) return;
    anime({
      targets: this.buttonContainer,
      alpha: 1,
      duration: 300,
      easing: "easeOutQuad",
    });
  }

  hideButton() {
    if (!this.buttonContainer) return;
    anime({
      targets: this.buttonContainer,
      alpha: 0,
      duration: 300,
      easing: "easeOutQuad",
    });
  }

  // ── Sequence (FS intro) ──

  startSequence() {
    return new Promise((resolve) => {
      if (this.isPlaying || !this.introSpine?.state) {
        resolve();
        return;
      }

      this.isPlaying = true;
      this.isWaitingForClick = false;
      this.canSkipAnimation = false;
      this.overlayRef.current?.fadeInBackground();
      this._clearTimers();

      soundManager.play(SOUND_NAME.free_spin_intro);

      if (this.symbolHeight > 0 && this.dimensions) this.applyScaleAndCenter();

      this.introSpine.state.clearTracks();
      this.introSpine.state.clearListeners();
      this.introSpine.state.apply(this.introSpine.skeleton);
      this.introSpine.skeleton.setToSetupPose();

      const inName = this._resolveAnim(ANIM.IN);

      // Input unlocks when the spine has revealed the tap word.
      this.skipProtectionTimeout = new TickerTimeout(
        () => {
          if (!this.isPlaying) return;
          this.canSkipAnimation = true;
          this.showButton();
          this.isWaitingForClick = true;
          this._armAutoHide();
        },
        this._revealMs?.(inName) ?? 1000,
      );

      // `in` while still hidden, shown next frame — no warm-up pose flash.
      this.introSpine.autoUpdate = true;
      this.introSpine.state.setAnimation(0, inName, false);
      if (this._showRAF) cancelAnimationFrame(this._showRAF);
      this._showRAF = requestAnimationFrame(() => {
        this._showRAF = null;
        if (this.isPlaying && this.introSpine) this.introSpine.alpha = 1;
      });

      this.currentTimeout = new TickerTimeout(() => {
        if (!this.isPlaying) return;
        this.introSpine.state.setAnimation(
          0,
          this._resolveAnim(ANIM.IDLE),
          true,
        );
        resolve();
      }, this.getSpineAnimationDuration(inName));
    });
  }

  handleUserClick() {
    if (!this.isPlaying || !this.introSpine?.state) return false;
    if (
      this.debounceTimeout ||
      !this.canSkipAnimation ||
      !this.isWaitingForClick
    ) {
      return false;
    }
    this.debounceTimeout = new TickerTimeout(() => {
      this.debounceTimeout = null;
    }, 300);
    this.playClosingAnimation();
    return true;
  }

  _armAutoHide() {
    if (this.isReload) return; // the player resumes it, not a timer
    if (this.autoHideTimeout) this.autoHideTimeout.clear();
    this.autoHideTimeout = new TickerTimeout(() => {
      this.autoHideTimeout = null;
      if (!this.isPlaying || !this.isWaitingForClick) return;
      this.playClosingAnimation();
    }, AUTO_HIDE_MS);
  }

  playClosingAnimation() {
    if (!this.isWaitingForClick || !this.introSpine?.state) return;
    if (this.autoHideTimeout) {
      this.autoHideTimeout.clear();
      this.autoHideTimeout = null;
    }

    this.isWaitingForClick = false;
    this.hideButton();
    soundManager.stop(SOUND_NAME.free_spin_intro);
    soundManager.play(SOUND_NAME.free_spin_intro_close);

    const outName = this._resolveAnim(ANIM.OUT);
    this.introSpine.state.clearListeners();
    this.introSpine.autoUpdate = true;
    this.introSpine.state.setAnimation(0, outName, false);
    this.introSpine.state.addListener({
      complete: (entry) => {
        if (entry.animation.name !== outName || !this.isPlaying) return;
        // Hand over to the wipe; its midpoint swaps the rooms (FS intro).
        // Not on a reload: the room is already on screen.
        // if (!this.isReload) this.dispatch(setShowTransitionDialog(true));
        // Only for a REAL trigger — the dev panel's preview (nothing
        // triggered) just closes instead of wiping into an empty room.
        if (!this.isReload && store.getState().holdAndWin?.triggered) {
          this.dispatch(setShowTransitionDialog(true));
        }
        this.finishAnimation();
      },
    });
  }

  finishAnimation() {
    if (!this.introSpine || !this.initialized) return;

    this.isPlaying = false;
    this.isWaitingForClick = false;
    this.canSkipAnimation = false;
    this.introSpine.alpha = 0;
    this.introSpine.autoUpdate = false;
    if (this._showRAF) {
      cancelAnimationFrame(this._showRAF);
      this._showRAF = null;
    }
    soundManager.stop(SOUND_NAME.free_spin_intro);
    this._clearTimers();

    this.overlayRef.current?.fadeOutBackground(() => {
      if (!this.dispatch) return;
      if (this.isReload) {
        // Only the popup goes. The round stays parked on the glowing button
        // until the player presses it (Mad Labs) — un-parking it here would
        // let the respin loop start by itself:
        this.dispatch(setShowHoldAndWinReload(false));
        // this.dispatch(clearHoldAndWinReload());
        return;
      }
      this.dispatch(setShowHoldAndWinIntro(false));
    });
  }

  _clearTimers() {
    for (const key of [
      "currentTimeout",
      "autoHideTimeout",
      "skipProtectionTimeout",
      "debounceTimeout",
    ]) {
      if (this[key]) {
        this[key].clear();
        this[key] = null;
      }
    }
  }

  cleanup() {
    if (this._showRAF) {
      cancelAnimationFrame(this._showRAF);
      this._showRAF = null;
    }
    soundManager.stop(SOUND_NAME.free_spin_intro);
    this._clearTimers();

    this.introSpine?.removeSlotObjects?.();
    if (this.buttonContainer) {
      this.buttonContainer.destroy({ children: true });
      this.buttonContainer = null;
      this.buttonText = null;
    }
    for (const container of this.textContainers)
      container.destroy({ children: true });
    this.textContainers = [];

    if (this.introSpine) {
      this.introSpine.state?.clearListeners();
      this.introSpine.state?.clearTracks();
      if (this.container?.children.includes(this.introSpine)) {
        this.container.removeChild(this.introSpine);
      }
      this.introSpine.destroy();
      this.introSpine = null;
    }
    this.initialized = false;
  }
}

export default HoldAndWinIntroController;
